/** @file IK from one bone: a target under the root at the bone's tip, then ik.create (suggestIk's shape). */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { Pose, SkeletonData } from '@pose2d/core/types';
import { parentSpace } from '@pose2d/editor-geometry/parentSpace';
import { slugifyName } from '@pose2d/format/slugifyName';
import type { Doc } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import type { Vec2 } from '@pose2d/math/types';
import type { ToolContext } from '../../panels/viewport/viewportTypes';
import type { PendingCommand } from '../../stores/types';
import { roundValue } from '../transform/roundValue';

const W = POSE_LAYOUT.WORLD_STRIDE;

/** [parent, end], or [end] when the parent is the root, missing or not a plain bone. */
function chainOf(data: SkeletonData, end: number, root: number): number[] {
  const parent = data.bones[end]?.parent ?? -1;
  return parent < 0 || parent === root || data.bones[parent]?.kind !== 'bone' ? [end] : [parent, end];
}

/** The world tip of data bone `end`, in the root's local space. */
function tipUnderRoot(data: SkeletonData, pose: Pose, end: number, root: number): Vec2 {
  const tip = affine.apply(pose.world.subarray(end * W, end * W + W), data.bones[end]?.length ?? 0, 0, [0, 0]);
  return parentSpace.point(pose.world.slice(root * W, root * W + W), tip[0], tip[1], [0, 0]);
}

/** `ik-<bone>`, unique against bone and IK names (one name for the target bone and the constraint). */
function ikName(doc: Doc, bone: string): string {
  const taken = new Set([...Object.values(doc.bones), ...Object.values(doc.constraints)].map((e) => e.name));
  return slugifyName(`ik-${bone}`, taken, 0).name;
}

/**
 * Chain [parent, bone], or [bone] when the parent is the root or not a plain bone (R-E1-13). No client id (W3-11):
 * ik.create names its target by the new bone's name.
 */
export function createIkCommands(ctx: Pick<ToolContext, 'data' | 'pose' | 'maps' | 'doc'>, end: number): PendingCommand[] {
  const { data, pose, maps, doc } = ctx;
  const root = data.bones.findIndex((b) => b.parent < 0);
  const local = tipUnderRoot(data, pose, end, root);
  const name = ikName(doc, data.bones[end]?.name ?? 'bone');
  return [
    { name: 'bone.create', input: { name, parent: maps.boneIds[root] ?? '', setup: { x: roundValue(local[0]), y: roundValue(local[1]) } }, ids: [] },
    { name: 'ik.create', input: { name, bones: chainOf(data, end, root).map((i) => maps.boneIds[i] ?? ''), target: name }, ids: [] },
  ];
}
